import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Coins, Image as ImageIcon, Sparkles, Trash2, TrendingUp } from 'lucide-react';
import api, { errMsg, fileUrl } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/context/LanguageContext';
import { ImageDrop } from '@/components/ImageDrop';
import { Button } from '@/components/ui/button';
import {
  BuyCreditsDialog,
  PaymentRequestList,
  RedeemCouponBox,
} from '@/components/payments/BuyCredits';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export default function Account() {
  const { user, refresh } = useAuth();
  const { t, lang } = useI18n();
  const [photos, setPhotos] = useState([]);
  const [info, setInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [txs, setTxs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [p, pi, s, tx, r] = await Promise.all([
        api.get('/photos'),
        api.get('/payment-info'),
        api.get('/stats'),
        api.get('/credits/transactions'),
        api.get('/payment-requests'),
      ]);
      setPhotos(p.data.photos);
      setInfo(pi.data);
      setStats(s.data);
      setTxs(tx.data.transactions);
      setRequests(r.data.requests);
    } catch (e) {
      toast.error(errMsg(e, t('account.loadFailed')));
    }
  }, [t]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const afterCredit = async () => {
    await refresh();
    await loadAll();
  };

  const upload = async (files) => {
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        await api.post('/photos', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      const { data } = await api.get('/photos');
      setPhotos(data.photos);
      toast.success(t('account.photosAdded'));
    } catch (e) {
      toast.error(errMsg(e, t('account.photoUploadFailed')));
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (id) => {
    try {
      await api.delete(`/photos/${id}`);
      setPhotos((p) => p.filter((x) => x.id !== id));
      toast.success(t('account.photoDeleted'));
    } catch (e) {
      toast.error(errMsg(e, t('account.deleteFailed')));
    }
  };

  const statCards = [
    { label: t('account.statCredits'), value: user?.credits ?? 0, icon: Coins },
    { label: t('account.statTotal'), value: stats?.total ?? 0, icon: TrendingUp },
    { label: t('account.statDone'), value: stats?.done ?? 0, icon: Sparkles },
    { label: t('account.statPhotos'), value: photos.length, icon: ImageIcon },
  ];

  const locale = lang === 'en' ? 'en-US' : 'tr-TR';

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">{t('account.title')}</h1>
        <p className="mt-1 text-sm text-[hsl(var(--text-muted))]">
          {user?.name} - {user?.email}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {statCards.map((s) => (
          <div key={s.label} className="panel p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-[hsl(var(--text-muted))]">{s.label}</span>
              <s.icon className="h-4 w-4 text-[hsl(var(--brand-red))]" />
            </div>
            <p className="mt-2 font-display text-2xl font-semibold">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Credits */}
      <section className="panel p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-lg font-semibold">{t('account.creditsTitle')}</h2>
            <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-[hsl(var(--text-muted))]">
              {t('account.creditsDesc')}
            </p>
          </div>
          <Button
            data-testid="buy-credits-open-button"
            onClick={() => setDialogOpen(true)}
            className="h-11 bg-[hsl(var(--brand-red))] px-5 font-semibold text-white hover:bg-[hsl(var(--brand-red-2))] active:scale-[0.98]"
          >
            <Coins className="mr-2 h-4 w-4" /> {t('account.buyButton')}
          </Button>
        </div>

        <div className="mt-5">
          <RedeemCouponBox onDone={afterCredit} />
        </div>

        <PaymentRequestList requests={requests} />

        {txs.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold">{t('account.txTitle')}</h3>
            <div
              data-testid="credit-transactions-list"
              className="mt-3 divide-y divide-[hsl(var(--border-soft))] rounded-[var(--radius-md)] border border-[hsl(var(--border-soft))]"
            >
              {txs.slice(0, 10).map((x) => (
                <div key={x.id} className="flex items-center justify-between gap-3 px-3.5 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-xs font-medium">{x.note}</p>
                    <p className="font-mono-alt text-[10px] text-[hsl(var(--text-muted))]">
                      {new Date(x.created_at).toLocaleString(locale)}
                    </p>
                  </div>
                  <span
                    className={[
                      'shrink-0 font-mono-alt text-sm font-semibold',
                      x.delta >= 0 ? 'text-[hsl(142,70%,55%)]' : 'text-[hsl(var(--brand-red))]',
                    ].join(' ')}
                  >
                    {x.delta > 0 ? `+${x.delta}` : x.delta}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Photos */}
      <section className="panel p-5 sm:p-6">
        <h2 className="font-display text-lg font-semibold">{t('account.photosTitle')}</h2>
        <p className="mt-1.5 text-sm text-[hsl(var(--text-muted))]">{t('account.photosDesc')}</p>

        <div className="mt-5">
          <ImageDrop
            testId="my-photos-upload-dropzone"
            multiple
            enablePaste={false}
            onFiles={upload}
            busy={uploading}
            title={t('account.uploadTitle')}
            hint={t('account.uploadHint')}
          />
        </div>

        {photos.length === 0 ? (
          <p data-testid="empty-state" className="mt-5 text-sm text-[hsl(var(--text-muted))]">
            {t('account.photosEmpty')}
          </p>
        ) : (
          <div className="mt-5 grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            {photos.map((p) => (
              <div
                key={p.id}
                data-testid={`account-photo-${p.id}`}
                className="group relative overflow-hidden rounded-[12px] border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))]"
                style={{ aspectRatio: '1' }}
              >
                <img src={fileUrl(p.url)} alt={p.name} className="h-full w-full object-cover" />
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      type="button"
                      data-testid={`my-photo-delete-button-${p.id}`}
                      className="absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-full bg-black/75 text-white opacity-0 transition-opacity duration-150 hover:bg-[hsl(var(--brand-red))] group-hover:opacity-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))]">
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('account.deletePhotoTitle')}</AlertDialogTitle>
                      <AlertDialogDescription className="text-[hsl(var(--text-muted))]">
                        {t('common.irreversible')}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))]">
                        {t('common.cancel')}
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => removePhoto(p.id)}
                        data-testid={`my-photo-delete-confirm-${p.id}`}
                        className="bg-[hsl(var(--brand-red))] text-white hover:bg-[hsl(var(--brand-red-2))]"
                      >
                        {t('common.delete')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        )}
      </section>

      <BuyCreditsDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        info={info}
        onDone={afterCredit}
      />
    </div>
  );
}
